import { formatCurrency } from '../format/currency.js';
import { addDays, addMonths, daysBetween, formatDate } from '../format/date.js';
import { formatInteger, formatPercent } from '../format/number.js';
import { COMPANY_REGISTRY_STATUS_LABELS } from '../labels/da.js';
import type { ReportValue, ReportValueType } from '../reports/report.js';
import type {
  CompanyRating,
  CompanyRegistryStatus,
  InvoiceOutcome,
  SignalArea,
  SignalLevel,
} from '../types/enums.js';
import { round2, sumMoney } from './money.js';

/**
 * The company check ("virksomhedstjek"): how likely a customer is to pay on our terms.
 *
 * Three sources, each judged into signals:
 *
 *   registry    the CVR register - is the company active, how old is it
 *   financials  the latest published annual report - equity ratio, liquidity, result, trend,
 *               the auditor's statement, and whether the report was filed on time
 *   payments    our own ledger in Business Central - what is overdue now, and how late the
 *               customer has paid before
 *
 * Every signal gets a level (good/ok/weak/bad) and a weight. The score is the weighted average
 * of the levels' points, and the rating follows from the score - except that a single severe
 * finding (bankruptcy, going-concern doubt, a missing annual report, long overdue invoices)
 * overrides a good average. The signals travel with the result, so the UI can show exactly
 * what the rating was built from.
 *
 * Pure functions with `today` as a parameter, so the API, the report and the tests agree.
 */

/* ------------------------------------------------------------------------------------------ */
/* Shapes                                                                                      */
/* ------------------------------------------------------------------------------------------ */

export interface CompanyRegistryInfo {
  name: string | null;
  status: CompanyRegistryStatus;
  /** A remark from the register beyond the plain status, e.g. "Konkursdekret ophævet". */
  status_text: string | null;
  founded_date: string | null;
  ended_date: string | null;
  /** "Anpartsselskab", "Enkeltmandsvirksomhed", ... as the register words it. */
  company_form: string | null;
  industry: string | null;
  employees: string | null;
}

/** One financial year from an annual report. Amounts in the report's currency, null when not filed. */
export interface AnnualFigures {
  period_start: string;
  period_end: string;
  revenue: number | null;
  gross_profit: number | null;
  operating_profit: number | null;
  profit_before_tax: number | null;
  profit_loss: number | null;
  equity: number | null;
  assets: number | null;
  current_assets: number | null;
  short_term_liabilities: number | null;
  cash: number | null;
  employees: number | null;
}

export interface AnnualReport {
  period_start: string;
  period_end: string;
  /** When the report was first made public. A later correction does not move it. */
  published_at: string;
  currency: string;
  /** "Regnskabsklasse B" etc. */
  reporting_class: string | null;
  /** "Revisionspåtegning", "Erklæring om review", ... */
  auditor_assistance: string | null;
  /** The auditor's conclusion when it is modified, e.g. "Konklusion med forbehold". */
  auditor_opinion: string | null;
  /** The auditor's remark on going concern, when there is one. */
  going_concern: string | null;
  /** Newest first: the reporting year and, when the report has them, the comparative figures. */
  years: AnnualFigures[];
  /** The report as a human reads it, at Erhvervsstyrelsen. */
  document_url: string | null;
}

export interface CompanyFilings {
  /** Null when the company has no published annual report. */
  latest: AnnualReport | null;
  /** Every annual report period found, newest first, with its first publication. */
  filings: Array<{ period_end: string; published_at: string }>;
}

/** A customer ledger entry from Business Central, in local currency and signed as BC signs it. */
export interface LedgerEntry {
  entry_no: number;
  document_type: 'invoice' | 'payment' | 'credit_memo' | 'refund' | 'other';
  document_no: string;
  posting_date: string;
  due_date: string | null;
  amount: number;
  remaining: number;
  open: boolean;
}

/**
 * A detailed customer ledger entry (table 379, "Detailed Cust. Ledg. Entry"): one movement on a
 * ledger entry - its posting, or an application against another entry. BC publishes them in the
 * standard query `Power_BI_Customer_List` - see integrations/bc-ledger.ts.
 */
export interface DetailedLedgerEntry {
  entry_no: number;
  /** The ledger entry it moves - `Cust_Ledger_Entry_No`. */
  ledger_entry_no: number;
  /** The posting it belongs to. The entries on both sides of an application share it. */
  transaction_no: number;
  posting_date: string;
  /** Local currency, signed as BC signs it: an application against an invoice is negative. */
  amount: number;
}

/** One movement that settled part of an invoice, oldest first. */
export interface LedgerApplication {
  date: string;
  /** What it settled, incl. VAT, in local currency. Negative: an application undone. */
  amount: number;
  kind: 'payment' | 'credit' | 'other';
  /** The paying or crediting document - `INDB00001`, `BFSK1000`. Null when it cannot be told. */
  document_no: string | null;
}

export interface InvoicePaymentOutcome {
  document_no: string;
  posting_date: string;
  due_date: string | null;
  amount: number;
  remaining: number;
  /** The date the invoice was paid - its last payment. Null unless `outcome` is 'paid'. */
  settled_date: string | null;
  /** The date it was closed, by a payment or a credit memo. Null while it is open. */
  closed_date: string | null;
  /** Days from due date to payment. Negative when paid early. */
  days_late: number | null;
  /** Days past the due date for an open invoice. */
  days_overdue: number | null;
  /**
   * The part settled by a credit memo that did not cancel the whole invoice. Partner commission
   * reads it: a partly credited invoice was not paid in full.
   */
  credited_amount: number;
  /** The payments and credits on the invoice, oldest first. */
  applications: LedgerApplication[];
  /**
   * True when read from BC's application entries - `settleInvoicesExact`. False when estimated,
   * the payments matched to the oldest invoices first - `settleInvoices`.
   */
  exact: boolean;
  outcome: InvoiceOutcome;
}

export interface PaymentBehaviour {
  currency: string;
  terms_days: number;
  /** Posted invoices, not counting those cancelled by a credit memo. */
  invoice_count: number;
  /** Paid invoices within the history window. */
  settled_count: number;
  on_time_count: number;
  /** Days after due date, weighted by amount. Negative when the customer pays early. */
  avg_days_late: number | null;
  open_amount: number;
  overdue_amount: number;
  overdue_count: number;
  oldest_overdue_days: number | null;
  /** The newest invoices with their outcome, for display. */
  invoices: InvoicePaymentOutcome[];
  /** The payment dates are BC's own, not estimated - see `InvoicePaymentOutcome.exact`. */
  exact: boolean;
}

/** Ratios from one financial year, in percent. */
export interface CompanyKeyFigures {
  period_end: string;
  /** Soliditetsgrad: equity / assets. */
  equity_ratio: number | null;
  /** Likviditetsgrad: current assets / short-term liabilities. */
  current_ratio: number | null;
  /** Afkastningsgrad: operating profit / assets. */
  return_on_assets: number | null;
  /** Egenkapitalforrentning: profit / equity. Only when equity is positive. */
  return_on_equity: number | null;
}

export interface CompanySignal {
  key: string;
  area: SignalArea;
  /** Danish. */
  label: string;
  value: ReportValue;
  /** How `value` is formatted - the same types the reports use. */
  type: ReportValueType;
  level: SignalLevel;
  weight: number;
  /** Danish. What the value is and why it got its level. */
  explanation: string;
}

export interface CompanyCheckSource {
  key: 'cvr' | 'annual_reports' | 'bc_ledger';
  /** Danish. */
  label: string;
  ok: boolean;
  fetched_at: string | null;
  /** Danish. Why the source is missing or what to know about it. */
  note: string | null;
  url: string | null;
}

export interface CompanyCheck {
  customer_id: string;
  customer_name: string;
  cvr: string | null;
  rating: CompanyRating;
  /** 0-100. Null when the rating is 'unknown'. */
  score: number | null;
  /** Danish. One line: the weak points, or that there are none. */
  summary: string;
  /** Danish. Can we expect payment on our terms? */
  payment_expectation: string;
  signals: CompanySignal[];
  registry: CompanyRegistryInfo | null;
  annual_report: AnnualReport | null;
  /** Aligned with `annual_report.years`. */
  key_figures: CompanyKeyFigures[];
  payments: PaymentBehaviour | null;
  sources: CompanyCheckSource[];
  checked_at: string;
}

export interface CompanyChecksResponse {
  data: CompanyCheck[];
  generated_at: string;
  /** Danish. Caveats for the whole list, e.g. that BC is not configured. */
  notes: string[];
}

/* ------------------------------------------------------------------------------------------ */
/* Thresholds                                                                                  */
/* ------------------------------------------------------------------------------------------ */

const LEVEL_POINTS: Record<SignalLevel, number> = { good: 100, ok: 70, weak: 35, bad: 0 };

/** Score at or above which the rating is 'low' and 'medium' risk. Below 'medium' is 'high'. */
export const RATING_THRESHOLDS = { low: 75, medium: 50 } as const;

/**
 * Days after the due date a payment still counts as on time. BC posts a payment when the bank
 * statement is booked, which is typically a day or two after the money arrived.
 */
export const PAYMENT_GRACE_DAYS = 3;

/** How far back paid invoices count towards the payment history. */
export const PAYMENT_HISTORY_DAYS = 730;

/** Annual reports are due five months after the financial year ends (årsregnskabsloven § 138). */
export const FILING_DEADLINE_MONTHS = 5;

/** Company forms that must publish an annual report. I/S and K/S only must in special cases. */
const FILING_FORMS = /aktieselskab|anpartsselskab|iværksætterselskab|partnerselskab/i;

/** A bad level on one of these caps the rating at 'high', whatever the average says. */
const SEVERE_SIGNALS = new Set(['going_concern', 'overdue_now', 'filing']);

const RATING_ORDER: readonly CompanyRating[] = ['low', 'medium', 'high', 'critical'];

/* ------------------------------------------------------------------------------------------ */
/* Key figures                                                                                 */
/* ------------------------------------------------------------------------------------------ */

/** Part of whole in percent with one decimal. Null when either is missing or whole is not positive. */
function ratio(part: number | null, whole: number | null): number | null {
  if (part === null || whole === null) return null;
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) return null;
  return Math.round((part / whole) * 1000) / 10;
}

export function keyFigures(year: AnnualFigures): CompanyKeyFigures {
  return {
    period_end: year.period_end,
    equity_ratio: ratio(year.equity, year.assets),
    current_ratio: ratio(year.current_assets, year.short_term_liabilities),
    return_on_assets: ratio(year.operating_profit, year.assets),
    return_on_equity:
      year.equity !== null && year.equity > 0 ? ratio(year.profit_loss, year.equity) : null,
  };
}

/* ------------------------------------------------------------------------------------------ */
/* Payments                                                                                    */
/* ------------------------------------------------------------------------------------------ */

const chronological = (a: LedgerEntry, b: LedgerEntry): number =>
  a.posting_date.localeCompare(b.posting_date) || a.entry_no - b.entry_no;

interface Applied {
  date: string;
  entry_no: number;
  document_no: string;
  amount: number;
  kind: 'payment' | 'credit';
}

/** The part of a payment or credit memo that has been applied against invoices. */
function applied(entry: LedgerEntry, kind: Applied['kind']): Applied {
  return {
    date: entry.posting_date,
    entry_no: entry.entry_no,
    document_no: entry.document_no,
    amount: round2(Math.abs(entry.amount) - Math.abs(entry.remaining)),
    kind,
  };
}

/** The latest date among the applications that settled something, or null. */
function lastDate(applications: readonly LedgerApplication[], kind?: LedgerApplication['kind']) {
  let last: string | null = null;
  for (const application of applications) {
    if (application.amount <= 0 || (kind && application.kind !== kind)) continue;
    if (last === null || application.date > last) last = application.date;
  }
  return last;
}

/**
 * Each invoice with an **estimate** of when it was paid - the fallback when BC's application
 * entries cannot be read; see `settleInvoicesExact`.
 *
 * The ledger entries alone do not say which payment closed which invoice. So the applied payments
 * are matched to invoices oldest first (FIFO), which is how most customers pay and how most
 * dunning works. A customer who pays a newer invoice before an older one gets the dates swapped,
 * but the average lateness stays roughly right.
 *
 * A credit memo for an invoice's exact amount cancels that invoice: it was never paid and says
 * nothing about payment behaviour. Any other credit memo reduces the oldest invoices like a
 * payment, but an invoice settled by credit alone does not count as paid.
 */
export function settleInvoices(
  entries: readonly LedgerEntry[],
  today: string,
): InvoicePaymentOutcome[] {
  const invoices = entries
    .filter((entry) => entry.document_type === 'invoice' && entry.amount > 0)
    .sort(chronological);

  /** Invoice entry -> the credit memo that cancelled it. */
  const credited = new Map<number, LedgerEntry>();
  const looseCredits: LedgerEntry[] = [];
  const memos = entries
    .filter((entry) => entry.document_type === 'credit_memo' && entry.amount < 0)
    .sort(chronological);

  for (const memo of memos) {
    let target: LedgerEntry | undefined;
    // The newest closed invoice of the same amount, posted no later than the memo.
    for (let index = invoices.length - 1; index >= 0; index -= 1) {
      const invoice = invoices[index]!;
      if (
        !invoice.open &&
        !credited.has(invoice.entry_no) &&
        invoice.posting_date <= memo.posting_date &&
        Math.abs(invoice.amount + memo.amount) < 0.01
      ) {
        target = invoice;
        break;
      }
    }
    if (target) credited.set(target.entry_no, memo);
    else looseCredits.push(memo);
  }

  const supply = [
    ...entries
      .filter((entry) => entry.document_type === 'payment')
      .map((entry) => applied(entry, 'payment')),
    ...looseCredits.map((entry) => applied(entry, 'credit')),
  ]
    .filter((item) => item.amount > 0.005)
    .sort((a, b) => a.date.localeCompare(b.date) || a.entry_no - b.entry_no);

  let cursor = 0;
  let left = supply[0]?.amount ?? 0;

  return invoices.map((invoice): InvoicePaymentOutcome => {
    const base = {
      document_no: invoice.document_no,
      posting_date: invoice.posting_date,
      due_date: invoice.due_date,
      amount: invoice.amount,
      remaining: invoice.remaining,
      settled_date: null,
      closed_date: null,
      days_late: null,
      days_overdue: null,
      credited_amount: 0,
      applications: [] as LedgerApplication[],
      exact: false,
    };

    const memo = credited.get(invoice.entry_no);
    if (memo) {
      return {
        ...base,
        outcome: 'credited',
        closed_date: memo.posting_date,
        applications: [
          {
            date: memo.posting_date,
            amount: invoice.amount,
            kind: 'credit',
            document_no: memo.document_no,
          },
        ],
      };
    }

    // The part of the invoice that has been settled, open or not - a partly paid open invoice
    // still takes its share of the payments.
    let need = round2(invoice.amount - invoice.remaining);
    let creditTaken = 0;
    const applications: LedgerApplication[] = [];

    while (need > 0.005 && cursor < supply.length) {
      const current = supply[cursor]!;
      const take = Math.min(need, left);
      need = round2(need - take);
      left = round2(left - take);
      applications.push({
        date: current.date,
        amount: take,
        kind: current.kind,
        document_no: current.document_no,
      });
      if (current.kind === 'credit') creditTaken = round2(creditTaken + take);
      if (left <= 0.005) {
        cursor += 1;
        left = supply[cursor]?.amount ?? 0;
      }
    }

    const settled = { ...base, credited_amount: creditTaken, applications };

    if (invoice.open) {
      const due = invoice.due_date;
      if (due && due < today) {
        return { ...settled, outcome: 'overdue', days_overdue: daysBetween(due, today) };
      }
      return { ...settled, outcome: 'open' };
    }

    // Closed, but the applied payments ran out - closed by something the ledger extract does
    // not explain. Left out rather than guessed.
    if (need > 0.005) return { ...settled, outcome: 'unresolved' };

    const paidOn = lastDate(applications, 'payment');
    const closedOn = lastDate(applications);
    if (!paidOn) return { ...settled, outcome: 'credited', closed_date: closedOn };

    return {
      ...settled,
      outcome: 'paid',
      settled_date: paidOn,
      closed_date: closedOn,
      days_late: daysBetween(invoice.due_date ?? invoice.posting_date, paidOn),
    };
  });
}

/**
 * Each invoice with **when and by what it was settled**, read from BC's application entries -
 * the detailed ledger, table 379 (decided 08-10-2026).
 *
 * Every movement on an invoice's ledger entry is a detailed entry. The first is the posting
 * itself - BC writes it before anything can be applied - and every later one is an application:
 * its posting date is when it happened, and its amount (negative) what it settled. A positive one
 * is an application undone, and takes its amount back.
 *
 * **What settled it is the entry on the other side.** An application moves both entries in one
 * transaction, so the other ledger entries in the same transaction are the paying or crediting
 * document: a payment (`INDB00001`) is paid, a credit memo (`BFSK1000`) is credited, and an invoice
 * closed by a credit memo is credited and not paid - BC calls both `Paid`.
 *
 * Two things it cannot tell, both rare here: a payment and a credit memo applied to the same
 * invoice in one go count as the payment, and a payment discount counts as paid. The query has no
 * entry type to tell them by.
 *
 * `entries` must hold the entries on the other side - the customer's, or all of them.
 */
export function settleInvoicesExact(
  entries: readonly LedgerEntry[],
  details: readonly DetailedLedgerEntry[],
  today: string,
): InvoicePaymentOutcome[] {
  const byEntry = new Map(entries.map((entry) => [entry.entry_no, entry]));
  const byLedger = new Map<number, DetailedLedgerEntry[]>();
  const byTransaction = new Map<number, DetailedLedgerEntry[]>();
  for (const detail of details) {
    byLedger.set(detail.ledger_entry_no, [...(byLedger.get(detail.ledger_entry_no) ?? []), detail]);
    byTransaction.set(detail.transaction_no, [
      ...(byTransaction.get(detail.transaction_no) ?? []),
      detail,
    ]);
  }

  /** The document on the other side of an application on `invoice`. */
  const settledBy = (invoice: LedgerEntry, row: DetailedLedgerEntry): LedgerApplication => {
    const others = (byTransaction.get(row.transaction_no) ?? [])
      .filter((other) => other.ledger_entry_no !== invoice.entry_no)
      .map((other) => byEntry.get(other.ledger_entry_no))
      .filter((entry): entry is LedgerEntry => entry !== undefined);
    const payment = others.find((entry) => entry.document_type === 'payment');
    const credit = others.find((entry) => entry.document_type === 'credit_memo');
    const source = payment ?? credit ?? others.find((entry) => entry.document_type !== 'invoice');

    return {
      date: row.posting_date,
      amount: round2(-row.amount),
      kind: payment ? 'payment' : credit ? 'credit' : 'other',
      document_no: source?.document_no ?? null,
    };
  };

  const invoices = entries
    .filter((entry) => entry.document_type === 'invoice' && entry.amount > 0)
    .sort(chronological);

  return invoices.map((invoice): InvoicePaymentOutcome => {
    const rows = [...(byLedger.get(invoice.entry_no) ?? [])].sort(
      (a, b) => a.entry_no - b.entry_no,
    );
    const applications = rows
      .slice(1)
      .map((row) => settledBy(invoice, row))
      .sort((a, b) => a.date.localeCompare(b.date));
    const total = (kind: LedgerApplication['kind']) =>
      round2(
        applications
          .filter((application) => application.kind === kind)
          .reduce((sum, application) => sum + application.amount, 0),
      );
    const paid = total('payment');

    const base = {
      document_no: invoice.document_no,
      posting_date: invoice.posting_date,
      due_date: invoice.due_date,
      amount: invoice.amount,
      remaining: invoice.remaining,
      settled_date: null,
      closed_date: null,
      days_late: null,
      days_overdue: null,
      credited_amount: Math.max(0, total('credit')),
      applications,
      exact: true,
    };

    if (invoice.open) {
      const due = invoice.due_date;
      if (due && due < today) {
        return { ...base, outcome: 'overdue', days_overdue: daysBetween(due, today) };
      }
      return { ...base, outcome: 'open' };
    }

    const closedOn = lastDate(applications);
    const paidOn = lastDate(applications, 'payment');
    if (paid > 0.005 && paidOn) {
      return {
        ...base,
        outcome: 'paid',
        settled_date: paidOn,
        closed_date: closedOn,
        days_late: daysBetween(invoice.due_date ?? invoice.posting_date, paidOn),
      };
    }
    if (base.credited_amount > 0.005)
      return { ...base, outcome: 'credited', closed_date: closedOn };
    // Closed, but by nothing the application entries show.
    return { ...base, outcome: 'unresolved', closed_date: closedOn };
  });
}

/**
 * The outcome of every invoice in the ledger, by invoice number in upper case.
 *
 * Exact when BC's application entries are given - see `settleInvoicesExact`. Without them,
 * estimated per customer: one customer's payments must not pay another's invoices.
 */
export function settleLedgerByInvoice(
  entries: ReadonlyArray<LedgerEntry & { customer_no: string }>,
  today: string,
  details?: readonly DetailedLedgerEntry[] | null,
): Map<string, InvoicePaymentOutcome> {
  const outcomes = new Map<string, InvoicePaymentOutcome>();
  const add = (list: readonly InvoicePaymentOutcome[]) => {
    for (const outcome of list) outcomes.set(outcome.document_no.trim().toUpperCase(), outcome);
  };

  if (details) {
    add(settleInvoicesExact(entries, details, today));
    return outcomes;
  }

  const byCustomer = new Map<string, LedgerEntry[]>();
  for (const entry of entries) {
    byCustomer.set(entry.customer_no, [...(byCustomer.get(entry.customer_no) ?? []), entry]);
  }
  for (const customerEntries of byCustomer.values()) add(settleInvoices(customerEntries, today));
  return outcomes;
}

/**
 * One customer's payment behaviour. Exact with BC's application entries (`details`), estimated
 * without them.
 */
export function paymentBehaviour(
  entries: readonly LedgerEntry[],
  input: {
    terms_days: number;
    currency: string;
    today: string;
    details?: readonly DetailedLedgerEntry[] | null;
  },
): PaymentBehaviour {
  const outcomes = input.details
    ? settleInvoicesExact(entries, input.details, input.today)
    : settleInvoices(entries, input.today);
  const since = addDays(input.today, -PAYMENT_HISTORY_DAYS);

  const paid = outcomes.filter(
    (item) => item.outcome === 'paid' && (item.settled_date ?? '') >= since,
  );
  const paidWeight = paid.reduce((total, item) => total + item.amount, 0);
  const avgDaysLate =
    paidWeight > 0
      ? Math.round(
          (paid.reduce((total, item) => total + item.amount * (item.days_late ?? 0), 0) /
            paidWeight) *
            10,
        ) / 10
      : null;

  const overdue = outcomes.filter((item) => item.outcome === 'overdue');
  const open = outcomes.filter((item) => item.outcome === 'open' || item.outcome === 'overdue');

  return {
    currency: input.currency,
    terms_days: input.terms_days,
    invoice_count: outcomes.filter((item) => item.outcome !== 'credited').length,
    settled_count: paid.length,
    on_time_count: paid.filter((item) => (item.days_late ?? 0) <= PAYMENT_GRACE_DAYS).length,
    avg_days_late: avgDaysLate,
    open_amount: sumMoney(open.map((item) => item.remaining)),
    overdue_amount: sumMoney(overdue.map((item) => item.remaining)),
    overdue_count: overdue.length,
    oldest_overdue_days:
      overdue.length > 0 ? Math.max(...overdue.map((item) => item.days_overdue ?? 0)) : null,
    invoices: [...outcomes].reverse().slice(0, 12),
    exact: Boolean(input.details),
  };
}

/* ------------------------------------------------------------------------------------------ */
/* Signals                                                                                     */
/* ------------------------------------------------------------------------------------------ */

function plural(count: number, one: string, many: string): string {
  return `${formatInteger(count)} ${count === 1 ? one : many}`;
}

function relativeToDue(days: number): string {
  const rounded = Math.round(days);
  if (rounded === 0) return 'på forfaldsdagen';
  return rounded < 0
    ? `${plural(-rounded, 'dag', 'dage')} før forfald`
    : `${plural(rounded, 'dag', 'dage')} efter forfald`;
}

function registrySignals(registry: CompanyRegistryInfo | null, today: string): CompanySignal[] {
  if (!registry) return [];
  const signals: CompanySignal[] = [];

  const explanation: Record<CompanyRegistryStatus, string> = {
    active: registry.status_text
      ? `Virksomheden er aktiv. Registret bemærker: ${registry.status_text}.`
      : 'Virksomheden er aktiv, og der er ikke afsagt konkursdekret.',
    distress: 'Der er afsagt konkursdekret over virksomheden.',
    bankrupt: 'Virksomheden er erklæret konkurs.',
    ceased: `Virksomheden er ophørt${registry.ended_date ? ` ${formatDate(registry.ended_date)}` : ''}.`,
  };

  signals.push({
    key: 'registry_status',
    area: 'registry',
    label: 'Status i CVR',
    value: registry.status_text ?? COMPANY_REGISTRY_STATUS_LABELS[registry.status],
    type: 'text',
    level: registry.status === 'active' ? (registry.status_text ? 'ok' : 'good') : 'bad',
    weight: registry.status === 'active' ? 1 : 3,
    explanation: explanation[registry.status],
  });

  if (registry.founded_date) {
    const years = Math.max(0, Math.floor(daysBetween(registry.founded_date, today) / 365.25));
    signals.push({
      key: 'company_age',
      area: 'registry',
      label: 'Alder (år)',
      value: years,
      type: 'integer',
      level: years >= 5 ? 'good' : years >= 2 ? 'ok' : 'weak',
      weight: 1,
      explanation:
        `Stiftet ${formatDate(registry.founded_date)}. Unge virksomheder har oftere ` +
        'betalingsproblemer; fra 5 år regnes virksomheden som etableret.',
    });
  }

  return signals;
}

function filingSignal(filings: CompanyFilings, latest: AnnualReport, today: string): CompanySignal {
  const deadline = addMonths(latest.period_end, FILING_DEADLINE_MONTHS);
  const nextDeadline = addMonths(addMonths(latest.period_end, 12), FILING_DEADLINE_MONTHS);
  const published = (
    filings.filings.find((filing) => filing.period_end === latest.period_end)?.published_at ??
    latest.published_at
  ).slice(0, 10);
  const base = {
    key: 'filing',
    area: 'financials' as const,
    label: 'Indsendelse af regnskab',
    value: latest.period_end,
    type: 'date' as const,
    weight: 2,
  };

  // A month's grace past the next deadline: Erhvervsstyrelsen's register lags a little, and a
  // report filed on the last day should not flag the company the morning after.
  if (today > addDays(nextDeadline, 30)) {
    return {
      ...base,
      level: 'bad',
      explanation:
        `Regnskabet for året efter ${formatDate(latest.period_end)} skulle have været ` +
        `offentliggjort senest ${formatDate(nextDeadline)}, men findes ikke. Et manglende ` +
        'regnskab er ofte et tegn på økonomiske problemer.',
    };
  }

  const lateDays = daysBetween(deadline, published);
  if (lateDays > 14) {
    return {
      ...base,
      level: 'weak',
      explanation:
        `Regnskabet pr. ${formatDate(latest.period_end)} blev offentliggjort ` +
        `${formatDate(published)}, ${plural(lateDays, 'dag', 'dage')} efter fristen.`,
    };
  }

  return {
    ...base,
    level: 'good',
    explanation:
      `Regnskabet pr. ${formatDate(latest.period_end)} blev offentliggjort ` +
      `${formatDate(published)}, inden for fristen.`,
  };
}

function financialSignals(
  filings: CompanyFilings | null,
  registry: CompanyRegistryInfo | null,
  today: string,
): CompanySignal[] {
  if (!filings) return [];

  const latest = filings.latest;
  if (!latest) {
    const obliged = registry?.company_form ? FILING_FORMS.test(registry.company_form) : false;
    const age = registry?.founded_date ? daysBetween(registry.founded_date, today) : null;
    // The first financial year can run 18 months and is due five months later.
    if (!obliged || age === null || age <= 730) return [];
    return [
      {
        key: 'filing',
        area: 'financials',
        label: 'Indsendelse af regnskab',
        value: null,
        type: 'date',
        level: 'bad',
        weight: 2,
        explanation:
          `Et ${registry?.company_form?.toLowerCase() ?? 'selskab'} skal offentliggøre sin ` +
          'årsrapport, men der er ingen i Erhvervsstyrelsens register.',
      },
    ];
  }

  const signals: CompanySignal[] = [];
  const money = (value: number | null): string =>
    formatCurrency(value, latest.currency, { decimals: 0 });
  const [current, previous] = latest.years;

  if (current) {
    const figures = keyFigures(current);
    const asOf = `regnskab pr. ${formatDate(current.period_end)}`;

    if (figures.equity_ratio !== null) {
      const value = figures.equity_ratio;
      signals.push({
        key: 'equity_ratio',
        area: 'financials',
        label: 'Soliditetsgrad',
        value,
        type: 'percent',
        level: value < 0 ? 'bad' : value < 10 ? 'weak' : value < 30 ? 'ok' : 'good',
        weight: 3,
        explanation:
          `Egenkapital ${money(current.equity)} af en balance på ${money(current.assets)} ` +
          `(${asOf}). Fra 30 % er virksomheden solid; under 0 % er gælden større end aktiverne.`,
      });
    }

    if (figures.current_ratio !== null) {
      const value = figures.current_ratio;
      signals.push({
        key: 'current_ratio',
        area: 'financials',
        label: 'Likviditetsgrad',
        value,
        type: 'percent',
        level: value < 80 ? 'bad' : value < 100 ? 'weak' : value < 150 ? 'ok' : 'good',
        weight: 2,
        explanation:
          `Omsætningsaktiver ${money(current.current_assets)} mod kortfristet gæld ` +
          `${money(current.short_term_liabilities)} (${asOf}). Under 100 % kan den kortfristede ` +
          'gæld ikke betales af omsætningsaktiverne; fra 150 % er der god luft.',
      });
    }

    if (current.profit_loss !== null) {
      const now = current.profit_loss;
      const before = previous?.profit_loss ?? null;
      signals.push({
        key: 'profit',
        area: 'financials',
        label: 'Årets resultat',
        value: now,
        type: 'money',
        level:
          now < 0
            ? before !== null && before < 0
              ? 'bad'
              : 'weak'
            : before !== null && before < 0
              ? 'ok'
              : 'good',
        weight: 2,
        explanation:
          `Årets resultat ${money(now)}` +
          (before !== null ? `, året før ${money(before)}` : '') +
          '. Ét underskud er svagt; to i træk er dårligt.',
      });
    }

    // Revenue when both years have it - many small companies only report gross profit.
    const useRevenue = current.revenue !== null && (previous?.revenue ?? null) !== null;
    const now = useRevenue ? current.revenue : current.gross_profit;
    const before = useRevenue ? (previous?.revenue ?? null) : (previous?.gross_profit ?? null);
    if (now !== null && before !== null && before > 0) {
      const change = Math.round(((now - before) / before) * 1000) / 10;
      const what = useRevenue ? 'omsætning' : 'bruttofortjeneste';
      signals.push({
        key: 'trend',
        area: 'financials',
        label: `Udvikling i ${what}`,
        value: change,
        type: 'percent',
        level: change >= 0 ? 'good' : change >= -10 ? 'ok' : change >= -30 ? 'weak' : 'bad',
        weight: 1,
        explanation:
          `${useRevenue ? 'Omsætning' : 'Bruttofortjeneste'} ${money(now)} mod ${money(before)} ` +
          `året før (${formatPercent(change, 1)}).`,
      });
    }
  }

  const goingConcern = latest.going_concern?.trim().toLowerCase() ?? '';
  if (goingConcern.includes('usikkerhed')) {
    const material = !goingConcern.startsWith('ikke');
    signals.push({
      key: 'going_concern',
      area: 'financials',
      label: 'Fortsat drift',
      value: latest.going_concern,
      type: 'text',
      level: material ? 'bad' : 'weak',
      weight: material ? 3 : 2,
      explanation: material
        ? 'Revisor har påpeget væsentlig usikkerhed om, hvorvidt virksomheden kan fortsætte driften.'
        : 'Revisor har omtalt virksomhedens fortsatte drift, men vurderer ikke usikkerheden som væsentlig.',
    });
  }

  if (latest.auditor_opinion && /forbehold|afkræftende|manglende/i.test(latest.auditor_opinion)) {
    // An adverse conclusion says the accounts are misleading; a disclaimer that the auditor could
    // not form a view. Both mean the figures above cannot be relied on - worse than a reservation.
    const severe = /afkræftende|manglende/i.test(latest.auditor_opinion);
    signals.push({
      key: 'auditor_opinion',
      area: 'financials',
      label: 'Revisors konklusion',
      value: latest.auditor_opinion,
      type: 'text',
      level: severe ? 'bad' : 'weak',
      weight: 2,
      explanation: severe
        ? 'Revisor har afgivet en afkræftende eller manglende konklusion. Regnskabstallene ' +
          'ovenfor kan derfor ikke lægges til grund.'
        : 'Revisor har taget forbehold i sin konklusion på regnskabet.',
    });
  }

  signals.push(filingSignal(filings, latest, today));
  return signals;
}

function paymentSignals(payments: PaymentBehaviour | null): CompanySignal[] {
  if (!payments || payments.invoice_count === 0) return [];
  const signals: CompanySignal[] = [];
  const money = (value: number): string =>
    formatCurrency(value, payments.currency, { decimals: 0 });

  const oldest = payments.oldest_overdue_days;
  signals.push({
    key: 'overdue_now',
    area: 'payments',
    label: 'Forfaldent nu',
    value: payments.overdue_amount,
    type: 'money',
    level: oldest === null ? 'good' : oldest <= 14 ? 'ok' : oldest <= 45 ? 'weak' : 'bad',
    weight: 3,
    explanation:
      oldest === null
        ? 'Ingen fakturaer er over forfald.'
        : `${plural(payments.overdue_count, 'faktura', 'fakturaer')} på i alt ` +
          `${money(payments.overdue_amount)} er over forfald. Den ældste har været forfalden i ` +
          `${plural(oldest, 'dag', 'dage')}.`,
  });

  if (payments.settled_count > 0 && payments.avg_days_late !== null) {
    const average = payments.avg_days_late;
    const few = payments.settled_count < 3;
    signals.push({
      key: 'payment_timing',
      area: 'payments',
      label: 'Betalingstid',
      value: Math.round(average),
      type: 'days',
      level:
        average <= PAYMENT_GRACE_DAYS
          ? 'good'
          : average <= 10
            ? 'ok'
            : average <= 30
              ? 'weak'
              : 'bad',
      weight: few ? 1 : 3,
      explanation:
        `Betalt i snit ${relativeToDue(average)}, vægtet efter beløb. ` +
        `${formatInteger(payments.on_time_count)} af ${formatInteger(payments.settled_count)} ` +
        `betalte fakturaer kom senest ${PAYMENT_GRACE_DAYS} dage efter forfald.` +
        (payments.exact
          ? ' Betalingsdatoerne er Business Centrals udligninger.'
          : ' Estimeret: udligningerne i Business Central kunne ikke læses, så betalingerne er ' +
            'fordelt på de ældste fakturaer først.') +
        (few ? ' Kun få betalte fakturaer, så signalet vejer mindre.' : ''),
    });
  }

  return signals;
}

/* ------------------------------------------------------------------------------------------ */
/* The verdict                                                                                 */
/* ------------------------------------------------------------------------------------------ */

function worst(a: CompanyRating, b: CompanyRating): CompanyRating {
  return RATING_ORDER.indexOf(a) >= RATING_ORDER.indexOf(b) ? a : b;
}

const EXPECTATION_WITHOUT_HISTORY: Record<CompanyRating, string> = {
  low: 'Ingen betalingshistorik endnu · økonomien peger på betaling til tiden',
  medium: 'Ingen betalingshistorik endnu · følg op ved forfald',
  high: 'Ingen betalingshistorik endnu · overvej forudbetaling eller kortere kredit',
  critical: 'Forvent ikke betaling på normale vilkår',
  unknown: 'Hverken betalingshistorik eller regnskab at vurdere ud fra',
};

function paymentExpectation(rating: CompanyRating, payments: PaymentBehaviour | null): string {
  if (rating === 'critical') return EXPECTATION_WITHOUT_HISTORY.critical;

  const parts: string[] = [];
  if (payments && payments.settled_count > 0 && payments.avg_days_late !== null) {
    const days = Math.round(payments.avg_days_late);
    parts.push(
      days <= PAYMENT_GRACE_DAYS
        ? 'Betaler typisk til tiden'
        : `Betaler i snit ${plural(days, 'dag', 'dage')} efter forfald`,
    );
  } else {
    parts.push(EXPECTATION_WITHOUT_HISTORY[rating]);
  }

  if (payments && payments.overdue_count > 0) {
    parts.push(`${plural(payments.overdue_count, 'faktura', 'fakturaer')} over forfald nu`);
  }
  return parts.join(' · ');
}

function summarize(
  rating: CompanyRating,
  registry: CompanyRegistryInfo | null,
  signals: readonly CompanySignal[],
): string {
  if (rating === 'critical' && registry) {
    return registry.status === 'ceased'
      ? 'Virksomheden er ophørt'
      : registry.status === 'bankrupt'
        ? 'Virksomheden er erklæret konkurs'
        : 'Der er afsagt konkursdekret';
  }
  if (rating === 'unknown') return 'For lidt data til en vurdering';

  const weakest = [
    ...signals.filter((signal) => signal.level === 'bad'),
    ...signals.filter((signal) => signal.level === 'weak'),
  ];
  if (weakest.length === 0) return 'Ingen svage punkter';
  return `Svage punkter: ${weakest
    .slice(0, 3)
    .map((signal) => signal.label)
    .join(', ')}`;
}

export interface CompanyCheckInput {
  registry: CompanyRegistryInfo | null;
  /** Null when the annual reports could not be fetched; `latest: null` when none are filed. */
  filings: CompanyFilings | null;
  payments: PaymentBehaviour | null;
  today: string;
}

export interface CompanyEvaluation {
  rating: CompanyRating;
  score: number | null;
  summary: string;
  payment_expectation: string;
  signals: CompanySignal[];
  key_figures: CompanyKeyFigures[];
}

export function evaluateCompany(input: CompanyCheckInput): CompanyEvaluation {
  const signals = [
    ...registrySignals(input.registry, input.today),
    ...financialSignals(input.filings, input.registry, input.today),
    ...paymentSignals(input.payments),
  ];

  // The register alone says too little to rate a company: an active, old company can still be
  // insolvent. A score needs the accounts or our own payment history.
  const substantive = signals.some((signal) => signal.area !== 'registry');
  const weight = signals.reduce((total, signal) => total + signal.weight, 0);
  const score =
    substantive && weight > 0
      ? Math.round(
          signals.reduce((total, signal) => total + signal.weight * LEVEL_POINTS[signal.level], 0) /
            weight,
        )
      : null;

  let rating: CompanyRating;
  if (input.registry && input.registry.status !== 'active') {
    rating = 'critical';
  } else if (score === null) {
    rating = 'unknown';
  } else {
    rating =
      score >= RATING_THRESHOLDS.low
        ? 'low'
        : score >= RATING_THRESHOLDS.medium
          ? 'medium'
          : 'high';
    // One severe finding outweighs a good average; any bad finding rules out 'low'.
    if (signals.some((signal) => signal.level === 'bad' && SEVERE_SIGNALS.has(signal.key))) {
      rating = worst(rating, 'high');
    } else if (signals.some((signal) => signal.level === 'bad')) {
      rating = worst(rating, 'medium');
    }
  }

  return {
    rating,
    // A bankrupt or ceased company is not "80 out of 100" because its last accounts were fine.
    score: rating === 'critical' ? 0 : score,
    summary: summarize(rating, input.registry, signals),
    payment_expectation: paymentExpectation(rating, input.payments),
    signals,
    key_figures: input.filings?.latest?.years.map(keyFigures) ?? [],
  };
}
